import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Link as LinkIcon, Image as ImageIcon, Search, Loader2 } from 'lucide-react';
import { apiClient } from '@/services/api';
import type { Asset } from '@/types';
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface AssetManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
}

type TabType = 'upload' | 'url' | 'gallery';

const ImageWithFallback: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const { t } = useTranslation();
  const [hasError, setHasError] = React.useState(false);

  if (hasError) {
    return (
      <div className="flex items-center justify-center h-full bg-muted">
        <span className="text-xs text-muted-foreground">{t('editor.assetManager.failedToLoad')}</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover"
      onError={() => setHasError(true)}
    />
  );
};

export const AssetManager: React.FC<AssetManagerProps> = ({
  isOpen,
  onClose,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('upload');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Note: uploading state is still used for file uploads, but not for URL insertion

  // Load assets when gallery tab is opened
  useEffect(() => {
    if (isOpen && activeTab === 'gallery') {
      loadAssets();
    }
  }, [isOpen, activeTab]);

  const loadAssets = async () => {
    setLoading(true);
    try {
      const response = await apiClient.getAllImages(100); // Load up to 100 images
      if (response.ok && response.assets) {
        setAssets(response.assets);
      } else {
        toast.error(response.error || t('editor.assetManager.failedLoadImages'));
      }
    } catch (error) {
      console.error('Error loading assets:', error);
      toast.error(t('editor.assetManager.failedLoadImages'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('editor.assetManager.onlyImageFiles'));
      return;
    }

    // Validate file extension
    const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !validExtensions.includes(extension)) {
      toast.error(t('editor.assetManager.invalidFormat'));
      return;
    }

    setUploading(true);
    try {
      const response = await apiClient.uploadAsset(file);
      if (response.ok && response.asset) {
        toast.success(t('editor.assetManager.uploadSuccess'));
        onSelect(response.asset.url);
        onClose();
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        toast.error(response.error || t('editor.assetManager.failedUpload'));
      }
    } catch (error) {
      console.error('Error uploading asset:', error);
      toast.error(t('editor.assetManager.failedUpload'));
    } finally {
      setUploading(false);
    }
  };

  // OLD: Create asset from URL (commented out - creates asset record in database)
  // const handleUrlSubmit = async () => {
  //   if (!urlValue.trim()) {
  //     toast.error('Please enter a URL');
  //     return;
  //   }

  //   setUploading(true);
  //   try {
  //     const response = await apiClient.createAssetFromUrl({ url: urlValue.trim() });
  //     if (response.ok && response.asset) {
  //       toast.success('Image added successfully');
  //       onSelect(response.asset.url);
  //       onClose();
  //       setUrlValue('');
  //     } else {
  //       toast.error(response.error || 'Failed to add image from URL');
  //     }
  //   } catch (error) {
  //     console.error('Error creating asset from URL:', error);
  //     toast.error('Failed to add image from URL');
  //   } finally {
  //     setUploading(false);
  //   }
  // };

  // NEW: Simple URL insertion - just includes the URL as image in content (no asset record)
  const handleUrlSubmit = () => {
    if (!urlValue.trim()) {
      toast.error(t('editor.assetManager.enterUrl'));
      return;
    }

    // Basic URL validation
    try {
      const url = new URL(urlValue.trim());
      // Check if it's http or https
      if (!['http:', 'https:'].includes(url.protocol)) {
        toast.error(t('editor.assetManager.urlProtocol'));
        return;
      }

      // Insert the URL directly into the content
      onSelect(urlValue.trim());
      onClose();
      setUrlValue('');
      toast.success(t('editor.assetManager.urlAdded'));
    } catch {
      toast.error(t('editor.assetManager.invalidUrl'));
    }
  };

  const handleSelectAsset = (asset: Asset) => {
    onSelect(asset.url);
    onClose();
  };

  const filteredAssets = assets.filter((asset) =>
    asset.original_filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
    asset.filename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('editor.assetManager.insertImage')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 flex-1 min-h-0">
          {/* Tabs */}
          <div className="flex gap-2 border-b">
            <button
              onClick={() => setActiveTab('upload')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'upload'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Upload className="h-4 w-4 inline mr-2" />
              {t('editor.assetManager.upload')}
            </button>
            <button
              onClick={() => setActiveTab('url')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'url'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <LinkIcon className="h-4 w-4 inline mr-2" />
              {t('editor.assetManager.fromUrl')}
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'gallery'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <ImageIcon className="h-4 w-4 inline mr-2" />
              {t('editor.assetManager.gallery')}
            </button>
          </div>

          {/* Upload Tab */}
          {activeTab === 'upload' && (
            <div className="flex-1 flex flex-col items-center justify-center gap-4 py-8">
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 rounded-lg border-2 border-dashed border-muted-foreground/50 flex items-center justify-center">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium">{t('editor.assetManager.uploadAnImage')}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('editor.assetManager.supportedFormats')}
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="asset-upload"
                  disabled={uploading}
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  variant="outline"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('editor.assetManager.uploading')}
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      {t('editor.assetManager.chooseFile')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* URL Tab */}
          {activeTab === 'url' && (
            <div className="flex-1 flex flex-col gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="image-url">{t('editor.assetManager.imageUrl')}</Label>
                <Input
                  id="image-url"
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={urlValue}
                  onChange={(e) => setUrlValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleUrlSubmit();
                    }
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  {t('editor.assetManager.urlHint')}
                </p>
              </div>
              <Button
                onClick={handleUrlSubmit}
                disabled={!urlValue.trim()}
                className="w-full"
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                {t('editor.assetManager.insertImageUrl')}
              </Button>
            </div>
          )}

          {/* Gallery Tab */}
          {activeTab === 'gallery' && (
            <div className="flex-1 flex flex-col gap-4 min-h-0">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('editor.assetManager.searchImages')}
                  className="pl-9"
                />
              </div>

              {/* Gallery Grid */}
              {loading ? (
                <div className="flex-1 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : filteredAssets.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-center py-8">
                  <div>
                    <ImageIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-sm text-muted-foreground">
                      {searchQuery ? t('editor.assetManager.noImagesMatching') : t('editor.assetManager.noImagesYet')}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {t('editor.assetManager.uploadHint')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto border rounded-md p-4">
                  <div className="grid grid-cols-3 gap-4">
                    {filteredAssets.map((asset) => (
                      <div
                        key={asset.id}
                        onClick={() => handleSelectAsset(asset)}
                        className="group relative aspect-square border rounded-lg overflow-hidden cursor-pointer hover:border-primary transition-colors"
                      >
                        <ImageWithFallback
                          src={asset.url}
                          alt={asset.alt_text || asset.original_filename}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <div className="text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
                            {t('editor.assetManager.select')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

